import { createContext, useContext, useCallback, useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";

export const DashboardContext = createContext(null);

export function useDashboard() {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error("useDashboard must be used within Layout");
  return ctx;
}

const EMPTY_FILTERS = { category: "", severity: "", environment: "", tag: "" };
const FILTER_KEYS = ["category", "severity", "environment", "tag"];

function filtersFromParams(params) {
  return FILTER_KEYS.reduce((filters, key) => {
    filters[key] = params.get(key) || "";
    return filters;
  }, { ...EMPTY_FILTERS });
}

export function useDashboardData() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState(() => filtersFromParams(searchParams));
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [runbooks, setRunbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const debounceRef = useRef();

  // Keep filter state synchronized with the URL. This makes category/severity/
  // environment selections reliable after navigation and also gives each
  // filtered library view a shareable URL such as /?category=Database.
  useEffect(() => {
    const next = filtersFromParams(searchParams);
    setFilters((current) => {
      const changed = FILTER_KEYS.some((key) => current[key] !== next[key]);
      return changed ? next : current;
    });
  }, [searchParams]);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get("/meta");
      setMeta(data);
    } catch (e) {
      console.warn("Failed to load meta (non-blocking):", formatApiError(e));
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const loadRunbooks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (debounced) params.search = debounced;
      if (filters.category) params.category = filters.category;
      if (filters.severity) params.severity = filters.severity;
      if (filters.environment) params.environment = filters.environment;
      if (filters.tag) params.tag = filters.tag;
      const { data } = await api.get("/runbooks", { params });
      setRunbooks(data.runbooks || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [debounced, filters]);

  useEffect(() => {
    loadRunbooks();
  }, [loadRunbooks]);

  const setFilter = useCallback((key, value) => {
    if (!FILTER_KEYS.includes(key)) return;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  }, [setSearchParams]);

  const clearFilters = useCallback(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      FILTER_KEYS.forEach((key) => next.delete(key));
      return next;
    });
    setSearch("");
  }, [setSearchParams]);

  const reload = useCallback(() => {
    loadMeta();
    loadRunbooks();
  }, [loadMeta, loadRunbooks]);

  const hasFilters = Boolean(
    debounced || filters.category || filters.severity || filters.environment || filters.tag
  );

  return {
    meta,
    filters,
    setFilter,
    clearFilters,
    search,
    setSearch,
    query: debounced,
    runbooks,
    loading,
    error,
    reload,
    loadRunbooks,
    hasFilters,
  };
}
